<template>
  <div class="modal-overlay" @click="closeModal">
    <div class="modal-content" @click.stop>
      <!-- Header -->
      <header class="modal-header">
        <div class="header-content">
          <div class="header-icon">
            <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
              <path d="M9,16V10H5L12,3L19,10H15V16H9M5,20V18H19V20H5Z"/>
            </svg>
          </div>
          <div class="header-text">
            <h2>{{ $t('upload.addToMarker.title') }}</h2>
            <p>{{ $t('upload.addToMarker.description') }}</p>
          </div>
        </div>
        <button @click="closeModal" class="close-btn" aria-label="Close modal">
          <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
            <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"/>
          </svg>
        </button>
      </header>

      <!-- Alert Messages -->
      <div v-if="error" class="alert alert-error">
        <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12,2C17.53,2 22,6.47 22,12C22,17.53 17.53,22 12,22C6.47,22 2,17.53 2,12C2,6.47 6.47,2 12,2M15.59,7L12,10.59L8.41,7L7,8.41L10.59,12L7,15.59L8.41,17L12,13.41L15.59,17L17,15.59L13.41,12L17,8.41L15.59,7Z"/>
        </svg>
        <span>{{ error }}</span>
      </div>

      <div v-if="success" class="alert alert-success">
        <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12,2C17.53,2 22,6.47 22,12C22,17.53 17.53,22 12,22C6.47,22 2,17.53 2,12C2,6.47 6.47,2 12,2M17,9L15.59,7.58L10,13.17L8.41,11.59L7,13L10,16L17,9Z"/>
        </svg>
        <span>{{ success }}</span>
      </div>

      <form @submit.prevent="handleUpload" class="upload-form">
        <!-- File Upload Section -->
        <div class="form-section">
          <div class="form-group">
            <div class="file-upload-area" :class="{ 'drag-over': dragOver }">
              <input
                ref="fileInput"
                type="file"
                :accept="isMobile ? 'image/jpeg,image/jpg,image/png,image/webp,text/plain' : 'image/jpeg,image/jpg,image/png,image/webp'"
                multiple
                @change="handleFileSelect"
                @dragover.prevent="dragOver = true"
                @dragleave.prevent="dragOver = false"
                @drop.prevent="handleFileDrop"
                class="file-input"
                :class="{ 'file-input-disabled': selectedFiles.length > 0 }"
              />
              <div class="file-upload-content">
                <div v-if="selectedFiles.length === 0" class="upload-placeholder">
                  <div class="upload-icon">
                    <svg width="48" height="48" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                    </svg>
                  </div>
                  <h3>{{ $t('upload.addToMarker.selectPhotos') }}</h3>
                  <p>{{ $t('upload.addToMarker.description') }}</p>
                  <p class="upload-hint">{{ $t('upload.addToMarker.gpsNote') }}</p>
                </div>
                <div v-else class="files-preview">
                  <div class="multiple-files-preview">
                    <div class="files-header">
                      <h4>{{ $t('upload.files.selected', { count: selectedFiles.length }) }}</h4>
                      <div class="files-actions">
                        <button type="button" @click="clearFiles" class="btn btn-secondary btn-small">
                          {{ $t('upload.files.clearAll') }}
                        </button>
                      </div>
                    </div>
                    <div class="files-list">
                      <div v-for="(file, index) in selectedFiles" :key="index" class="file-item-with-tags">
                        <div class="file-preview-section">
                          <img v-if="previewUrls[index]" :src="previewUrls[index]" alt="Preview" class="file-thumbnail" />
                          <div class="file-details">
                            <span class="file-name">{{ file.name }}</span>
                            <span class="file-size">{{ formatFileSize(file.size) }}</span>
                          </div>
                        </div>
                        <div class="file-tags-section">
                          <div class="inherited-tags-display">
                            <label class="tags-label">{{ $t('upload.addToMarker.inheritedTags') }}:</label>
                            <div class="inherited-tags">
                              <span 
                                v-for="tag in defaultTags" 
                                :key="tag" 
                                class="inherited-tag"
                              >
                                {{ tag }}
                              </span>
                              <span v-if="defaultTags.length === 0" class="no-tags">
                                {{ $t('upload.addToMarker.noTagsInherited') }}
                              </span>
                            </div>
                            <p class="tags-note">{{ $t('upload.addToMarker.tagsNote') }}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="files-summary">
                      <div class="summary-row">
                        <span>{{ $t('upload.files.totalSize', { size: formatFileSize(totalFileSize) }) }}</span>
                      </div>
                      <div class="summary-row">
                        <span class="summary-label">{{ $t('upload.addToMarker.inheritedTags') }}:</span>
                        <div class="summary-tags">
                          <span 
                            v-for="tag in defaultTags" 
                            :key="tag" 
                            class="summary-tag"
                          >
                            {{ tag }}
                          </span>
                          <span v-if="defaultTags.length === 0" class="no-tags">
                            {{ $t('upload.addToMarker.noTagsInherited') }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Upload Progress -->
            <div v-if="uploading && uploadProgress.total > 0" class="upload-progress-section">
              <div class="progress-header">
                <h4>{{ $t('upload.progress.uploading', { total: uploadProgress.total }) }}</h4>
                <span>{{ $t('upload.progress.completed', { completed: uploadProgress.completed, total: uploadProgress.total }) }}</span>
              </div>
              <div class="progress-bar">
                <div class="progress-fill" :style="{ width: progressPercentage + '%' }"></div>
              </div>
              <div class="progress-details">
                <span v-if="uploadProgress.current">{{ uploadProgress.current }}</span>
                <span>{{ progressPercentage }}%</span>
              </div>
              <div class="progress-note">
                <p><strong>Note:</strong> Large images (>10MB) may take up to 10 minutes to process and resize. Please be patient.</p>
              </div>
              <div v-if="uploadProgress.errors.length > 0" class="progress-errors">
                <h5>{{ $t('upload.progress.errors', { count: uploadProgress.errors.length }) }}</h5>
                <ul>
                  <li v-for="error in uploadProgress.errors" :key="error.file">
                    <strong>{{ error.file }}:</strong> {{ error.message }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- Upload Button -->
        <div class="form-actions-centered">
          <button
            type="submit"
            class="btn btn-primary btn-large"
            :class="{ 'btn-disabled': !isFormValid || uploading }"
            :disabled="!isFormValid || uploading"
          >
            <svg v-if="uploading" width="20" height="20" fill="currentColor" viewBox="0 0 24 24" class="spinner">
              <path d="M12,4V2A10,10 0 0,0 2,12H4A8,8 0 0,1 12,4Z"/>
            </svg>
            <svg v-else width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
              <path d="M9,16V10H5L12,3L19,10H15V16H9M5,20V18H19V20H5Z"/>
            </svg>
            <span v-if="uploading">{{ $t('upload.form.uploading') }}</span>
            <span v-else-if="selectedFiles.length === 0">{{ $t('upload.validation.fileRequired') }}</span>
            <span v-else>{{ $t('upload.addToMarker.uploadButton', { count: selectedFiles.length }) }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { photosAPI } from '../services/api'

export default {
  name: 'MarkerUploadModal',
  props: {
    markerGroupId: {
      type: String,
      required: true
    },
    defaultTags: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      selectedFiles: [],
      previewUrls: [],
      uploading: false,
      error: '',
      success: '',
      dragOver: false,
      uploadProgress: {
        total: 0,
        completed: 0,
        current: '',
        errors: []
      }
    }
  },
  computed: {
    isFormValid() {
      return this.selectedFiles.length > 0
    },
    isMobile() {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    },
    totalFileSize() {
      return this.selectedFiles.reduce((total, file) => total + file.size, 0)
    },
    progressPercentage() {
      if (this.uploadProgress.total === 0) return 0
      return Math.round((this.uploadProgress.completed / this.uploadProgress.total) * 100)
    }
  },
  methods: {
    closeModal() {
      this.$emit('close')
    },

    handleFileSelect(event) {
      const files = Array.from(event.target.files)
      this.setSelectedFiles(files)
    },

    handleFileDrop(event) {
      this.dragOver = false
      const files = Array.from(event.dataTransfer.files)
      this.setSelectedFiles(files)
    },

    setSelectedFiles(files) {
      if (!files || files.length === 0) return

      this.clearFiles()

      const validFiles = []
      const errors = []
      const maxSize = 60 * 1024 * 1024 // 60MB for all devices

      for (const file of files) {
        if (!file) continue

        if (!file.type || !file.type.match(/^image\/(jpeg|jpg|png|webp)$/)) {
          if (files.length <= 5) {
            errors.push(`${file.name}: Only JPEG, PNG, or WebP files are allowed`)
          }
          continue
        }

        if (file.size > maxSize) {
          const maxSizeMB = '60MB'
          errors.push(`${file.name}: File size must be less than ${maxSizeMB}`)
          continue
        }

        validFiles.push(file)
      }

      if (errors.length > 0 && (files.length <= 5 || validFiles.length === 0)) {
        this.error = errors.join('; ')
        if (validFiles.length === 0) return
      } else {
        this.error = ''
      }

      this.selectedFiles = validFiles
      this.createPreviewUrls()
    },

    createPreviewUrls() {
      this.previewUrls = []
      const filesToPreview = this.selectedFiles.slice(0, 10)
      
      for (const file of filesToPreview) {
        try {
          const previewUrl = URL.createObjectURL(file)
          this.previewUrls.push(previewUrl)
        } catch (error) {
          console.error('Error creating preview URL:', error)
          this.previewUrls.push(null)
        }
      }
    },

    clearFiles() {
      this.selectedFiles = []
      
      for (const url of this.previewUrls) {
        if (url) {
          URL.revokeObjectURL(url)
        }
      }
      this.previewUrls = []
      
      this.$refs.fileInput.value = ''
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },

    async handleUpload() {
      if (this.selectedFiles.length === 0) {
        this.error = this.$t('upload.validation.fileRequired')
        return
      }

      // Check if we have inherited tags
      if (!this.defaultTags || this.defaultTags.length === 0) {
        this.error = 'No tags available from the original photo. Please try again.'
        return
      }

      this.uploading = true
      this.error = ''
      this.success = ''

      this.uploadProgress = {
        total: this.selectedFiles.length,
        completed: 0,
        current: '',
        errors: []
      }

      const successfulUploads = []
      const inheritedTagsString = this.defaultTags.join(', ')

      try {
        for (let i = 0; i < this.selectedFiles.length; i++) {
          const file = this.selectedFiles[i]
          this.uploadProgress.current = file.name

          try {
            const formData = new FormData()
            formData.append('photo', file)
            formData.append('markerGroupId', this.markerGroupId)
            formData.append('tags', inheritedTagsString)

            const response = await photosAPI.uploadToMarker(formData)
            successfulUploads.push(response.data)
            this.uploadProgress.completed++

          } catch (error) {
            console.error(`Upload error for ${file.name}:`, error)
            
            let errorMessage = error.response?.data?.message || 'Upload failed'
            
            // Handle specific error types
            if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
              errorMessage = 'Upload timeout - large images may take up to 10 minutes to process. Please check if the photo was saved successfully.'
            } else if (error.response?.status === 413) {
              errorMessage = 'File too large'
            } else if (error.response?.status === 502) {
              errorMessage = 'Server temporarily unavailable'
            } else if (error.response?.status === 504) {
              errorMessage = 'Server timeout - image may still be processing. Please check if the photo was saved.'
            }

            this.uploadProgress.errors.push({
              file: file.name,
              message: errorMessage
            })
          }
        }

        if (successfulUploads.length === this.selectedFiles.length) {
          this.success = this.$t('upload.progress.successAll', { count: successfulUploads.length })
          
          this.clearFiles()

          setTimeout(() => {
            this.$emit('upload-success', successfulUploads)
          }, 2000)
        } else if (successfulUploads.length > 0) {
          this.success = this.$t('upload.progress.successPartial', { 
            successful: successfulUploads.length, 
            total: this.selectedFiles.length 
          })
          this.$emit('upload-success', successfulUploads)
        } else {
          this.error = this.$t('upload.progress.failed')
        }

      } catch (error) {
        console.error('Batch upload error:', error)
        this.error = 'Upload process failed. Please try again.'
      } finally {
        this.uploading = false
        this.uploadProgress.current = ''
      }
    }
  }
}
</script>

<style scoped>
/* Use the same styles as UploadModal but with some modifications */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1.5rem;
  opacity: 0;
  animation: fadeIn 0.3s ease forwards;
}

@keyframes fadeIn {
  to {
    opacity: 1;
  }
}

.modal-content {
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 24px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
  position: relative;
  transform: translateY(20px) scale(0.95);
  animation: slideUp 0.3s ease 0.1s forwards;
}

@keyframes slideUp {
  to {
    transform: translateY(0) scale(1);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem 2rem 1.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%);
  border-radius: 24px 24px 0 0;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.header-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #22c55e 0%, #10b981 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 4px 12px rgba(34, 197, 94, 0.3);
}

.header-text h2 {
  margin: 0;
  color: #1f2937;
  font-size: 1.75rem;
  font-weight: 700;
  line-height: 1.2;
}

.header-text p {
  margin: 0.25rem 0 0;
  color: #6b7280;
  font-size: 0.875rem;
  font-weight: 500;
}

.close-btn {
  background: rgba(107, 114, 128, 0.1);
  border: 1px solid rgba(107, 114, 128, 0.2);
  border-radius: 10px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #6b7280;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.close-btn:hover {
  background: rgba(107, 114, 128, 0.2);
  transform: scale(1.05);
  color: #374151;
}

/* Alert Messages */
.alert {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  margin: 0 2rem 1rem;
  border-radius: 12px;
  font-weight: 500;
  font-size: 0.875rem;
  animation: slideUp 0.3s ease;
}

.alert-error {
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  color: #dc2626;
}

.alert-success {
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.2);
  color: #059669;
}

/* Form Styles */
.upload-form {
  padding: 2rem 2rem 2rem;
  display: flex;
  flex-direction: column;
}

.form-section {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

/* File Upload Area */
.file-upload-area {
  border: 2px dashed rgba(34, 197, 94, 0.3);
  border-radius: 16px;
  padding: 2.5rem;
  text-align: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  background: rgba(34, 197, 94, 0.02);
  cursor: pointer;
}

.file-upload-area:hover {
  border-color: rgba(34, 197, 94, 0.5);
  background: rgba(34, 197, 94, 0.05);
  transform: translateY(-2px);
}

.file-upload-area.drag-over {
  border-color: #22c55e;
  background: rgba(34, 197, 94, 0.1);
  transform: scale(1.02);
  box-shadow: 0 8px 32px rgba(34, 197, 94, 0.2);
}

.file-input {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0;
  cursor: pointer;
}

.file-input-disabled {
  pointer-events: none;
}

.upload-placeholder {
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.upload-icon {
  color: #22c55e;
  opacity: 0.7;
}

.upload-placeholder h3 {
  margin: 0;
  color: #1f2937;
  font-size: 1.25rem;
  font-weight: 600;
}

.upload-placeholder p {
  margin: 0;
  color: #6b7280;
  font-size: 0.875rem;
}

.upload-hint {
  color: #9ca3af !important;
  font-size: 0.8rem !important;
}

/* Multiple Files Preview */
.multiple-files-preview {
  text-align: left;
}

.files-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.files-header h4 {
  margin: 0;
  color: #1f2937;
  font-size: 1rem;
  font-weight: 600;
}

.files-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.files-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-height: 400px;
  overflow-y: auto;
  margin-bottom: 1rem;
}

.file-item-with-tags {
  padding: 1rem;
  background: rgba(0, 0, 0, 0.02);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 12px;
}

.file-preview-section {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.file-thumbnail {
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.file-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.file-details .file-name {
  font-weight: 600;
  color: #1f2937;
  font-size: 0.875rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-details .file-size {
  color: #6b7280;
  font-size: 0.75rem;
}

.file-tags-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.inherited-tags-display {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.tags-label {
  font-weight: 600;
  color: #1f2937;
  font-size: 0.875rem;
}

.inherited-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.inherited-tag {
  padding: 0.25rem 0.5rem;
  background: rgba(34, 197, 94, 0.05);
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #22c55e;
}

.no-tags {
  color: #9ca3af;
  font-size: 0.875rem;
  font-weight: 500;
}

.tags-note {
  color: #9ca3af;
  font-size: 0.875rem;
  font-weight: 500;
}

/* Upload Progress */
.upload-progress-section {
  margin: 1.5rem 0;
  padding: 1.5rem;
  background: rgba(34, 197, 94, 0.05);
  border: 1px solid rgba(34, 197, 94, 0.2);
  border-radius: 12px;
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.progress-header h4 {
  margin: 0;
  color: #1f2937;
  font-size: 1rem;
  font-weight: 600;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.75rem;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #22c55e, #10b981);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.progress-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.875rem;
  color: #1f2937;
  margin-bottom: 1rem;
}

.progress-note {
  margin-top: 0.5rem;
  padding: 0.5rem;
  background: rgba(34, 197, 94, 0.05);
  border: 1px solid rgba(34, 197, 94, 0.2);
  border-radius: 8px;
}

.progress-errors {
  margin-top: 1rem;
  padding: 1rem;
  background: rgba(239, 68, 68, 0.05);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 8px;
}

.progress-errors h5 {
  margin: 0 0 0.5rem 0;
  color: #dc2626;
  font-size: 0.875rem;
  font-weight: 600;
}

.progress-errors ul {
  margin: 0;
  padding-left: 1rem;
  list-style-type: disc;
}

.progress-errors li {
  color: #dc2626;
  font-size: 0.8rem;
  margin-bottom: 0.25rem;
}

/* Buttons */
.btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.875rem 1.5rem;
  border: none;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  font-family: inherit;
  text-decoration: none;
  position: relative;
  overflow: hidden;
}

.btn-primary {
  background: linear-gradient(135deg, #22c55e 0%, #10b981 100%);
  color: white;
  border: 1px solid transparent;
  box-shadow: 0 4px 6px -1px rgba(34, 197, 94, 0.3);
}

.btn-primary:hover:not(.btn-disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(34, 197, 94, 0.4);
}

.btn-secondary {
  background: rgba(107, 114, 128, 0.15);
  color: #1f2937;
  border: 1px solid rgba(107, 114, 128, 0.4);
  font-weight: 600;
}

.btn-secondary:hover:not(.btn-disabled) {
  background: rgba(107, 114, 128, 0.25);
  color: #111827;
  border-color: rgba(107, 114, 128, 0.6);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(107, 114, 128, 0.4);
}

.btn-large {
  padding: 1rem 2rem;
  font-size: 1rem;
  min-height: 48px;
  width: 100%;
}

.btn-small {
  padding: 0.5rem 0.75rem;
  font-size: 0.8rem;
}

.btn-disabled {
  opacity: 0.6;
  cursor: not-allowed !important;
  transform: none !important;
  box-shadow: none !important;
}

.btn svg.spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Centered Form Actions */
.form-actions-centered {
  display: flex;
  justify-content: center;
  margin: 2rem 0 1rem;
}

.form-actions-centered .btn-large {
  width: auto;
  min-width: 200px;
  max-width: 400px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .modal-overlay {
    padding: 1rem;
  }

  .modal-content {
    max-height: 80vh;
    border-radius: 20px;
  }

  .modal-header {
    padding: 1.5rem 1.5rem 1rem;
    border-radius: 20px 20px 0 0;
  }

  .header-content {
    gap: 0.75rem;
  }

  .header-icon {
    width: 40px;
    height: 40px;
  }

  .header-text h2 {
    font-size: 1.5rem;
  }

  .upload-form {
    padding: 1.5rem 1.5rem 1.5rem;
  }

  .file-upload-area {
    padding: 2rem 1.5rem;
  }

  .files-list {
    max-height: 300px;
  }

  .file-item-with-tags {
    padding: 1rem 0.75rem;
  }

  .file-thumbnail {
    width: 60px;
    height: 60px;
  }

  .file-details .file-name {
    font-size: 0.875rem;
  }

  .file-tags-input {
    padding: 0.875rem;
    font-size: 0.875rem;
  }

  .files-actions {
    flex-direction: column;
    gap: 0.5rem;
    align-items: stretch;
  }

  .files-actions .btn-small {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .modal-header {
    padding: 1rem;
  }

  .header-text h2 {
    font-size: 1.25rem;
  }

  .upload-form {
    padding: 1.5rem 1rem 1rem;
  }

  .file-upload-area {
    padding: 1.5rem 1rem;
  }

  .upload-placeholder h3 {
    font-size: 1.125rem;
  }

  .alert {
    margin: 1rem;
  }

  .form-actions-centered .btn-large {
    min-width: 160px;
    font-size: 0.875rem;
  }
}

/* Dark Mode Support */
@media (prefers-color-scheme: dark) {
  .modal-content {
    background: rgba(17, 24, 39, 0.95);
    border-color: rgba(255, 255, 255, 0.1);
  }

  .modal-header {
    background: linear-gradient(135deg, rgba(34, 197, 94, 0.2) 0%, rgba(16, 185, 129, 0.1) 100%);
    border-bottom-color: rgba(255, 255, 255, 0.1);
  }

  .header-text h2 {
    color: #f9fafb;
  }

  .header-text p {
    color: #9ca3af;
  }

  .close-btn {
    background: rgba(55, 65, 81, 0.3);
    border-color: rgba(255, 255, 255, 0.2);
    color: #9ca3af;
  }

  .close-btn:hover {
    background: rgba(55, 65, 81, 0.5);
    color: #f3f4f6;
  }

  .file-upload-area {
    border-color: rgba(34, 197, 94, 0.4);
    background: rgba(34, 197, 94, 0.05);
  }

  .file-upload-area:hover {
    background: rgba(34, 197, 94, 0.1);
  }

  .upload-placeholder h3 {
    color: #f9fafb;
  }

  .upload-placeholder p {
    color: #d1d5db;
  }

  .files-header h4 {
    color: #f9fafb;
  }

  .file-details .file-name {
    color: #f9fafb;
  }

  .file-details .file-size {
    color: #d1d5db;
  }

  .file-item-with-tags {
    background: rgba(55, 65, 81, 0.3);
    border-color: rgba(255, 255, 255, 0.2);
  }

  .file-tags-input {
    background: rgba(17, 24, 39, 0.8);
    border-color: rgba(255, 255, 255, 0.2);
    color: #f9fafb;
  }

  .file-tags-input:focus {
    background: rgba(17, 24, 39, 0.95);
    border-color: #22c55e;
    box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.2);
  }

  .file-tags-input::placeholder {
    color: #6b7280;
  }

  .progress-header h4 {
    color: #f9fafb;
  }

  .progress-details {
    color: #d1d5db;
  }

  .upload-placeholder h3 {
    color: #f9fafb;
  }

  .upload-placeholder p {
    color: #d1d5db;
  }

  .upload-hint {
    color: #9ca3af;
  }
}

.files-summary {
  padding: 0.75rem;
  background: rgba(34, 197, 94, 0.05);
  border-radius: 8px;
  color: #22c55e;
  font-weight: 500;
  font-size: 0.875rem;
}

.summary-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.summary-row:last-child {
  margin-bottom: 0;
}

.summary-label {
  font-weight: 600;
  color: #1f2937;
  min-width: fit-content;
}

.summary-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.summary-tag {
  padding: 0.125rem 0.375rem;
  background: rgba(34, 197, 94, 0.1);
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  color: #22c55e;
}
</style> 